import { useState } from 'react';
import { Save, Trash2 } from 'lucide-react';

import { paths } from 'configuration/paths';
import { TranslationsKeys } from 'types/translation';
import { Survey } from 'types/survey';
import { SurveyResponse } from 'types/surveyResponse';
import { DateTimeFormat, formatDateTime } from 'utils/date';
import { useTranslate } from 'hooks/useTranslate';
import { useSurveyTranslation } from 'hooks/useSurveyTranslation';
import { Header } from 'components/Header';
import { Modal } from 'components/Modal';
import { Spinner } from 'components/Spinner';
import styles from './SurveyHeader.module.scss';

interface IProps {
  isLoading: boolean;
  isError: boolean;
  surveyDefinition?: Survey;
  surveyResponse?: SurveyResponse;
  onDelete?: () => void;
}

export const SurveyHeader = ({ isLoading, isError, surveyDefinition, surveyResponse, onDelete }: IProps) => {
  const { t } = useTranslate();
  const ts = useSurveyTranslation(surveyDefinition);
  const [showDeleteSurveyConfirmation, setShowDeleteSurveyConfirmation] = useState(false);

  return (
    <>
      <Header
        navigationItems={
          onDelete
            ? [
                {
                  url: paths.mySurveys,
                  text: t(TranslationsKeys.surveyPage_closeAndContinueLater),
                  icon: Save
                },
                {
                  text: t(TranslationsKeys.surveyPage_deleteSurvey),
                  icon: Trash2,
                  onClick: () => setShowDeleteSurveyConfirmation(true)
                }
              ]
            : []
        }>
        {isLoading && <Spinner />}
        {isError && <div className="m-2 fs-6 text-danger">{t(TranslationsKeys.surveyPage_loadingError)}</div>}
        {surveyDefinition && surveyResponse && (
          <div className={styles.surveyHeader}>
            <h3 className={styles.surveyHeaderTitle}>{ts(surveyDefinition.name)}</h3>
            <div className={styles.surveyHeaderText}>
              {surveyResponse.customerName || t(TranslationsKeys.common_noCustomer)} &#x26ac;{' '}
              {formatDateTime(new Date(surveyResponse.createdAt), DateTimeFormat.MEDIUM)}
            </div>
          </div>
        )}
      </Header>

      {showDeleteSurveyConfirmation && (
        <Modal
          title={t(TranslationsKeys.surveyPage_deleteSurveyConfirmTitle)}
          description={
            <>
              {t(TranslationsKeys.surveyPage_deleteSurveyConfirmText1)}
              <strong>{surveyDefinition?.name ? ts(surveyDefinition!.name) : ''}</strong>
              {t(TranslationsKeys.surveyPage_deleteSurveyConfirmText2)}
            </>
          }
          primaryButtonLabel={
            <div className="d-flex justify-content-center align-items-center gap-2">
              <Trash2 />
              {t(TranslationsKeys.common_delete)}
            </div>
          }
          primaryButtonVariant="danger"
          secondaryButtonLabel={t(TranslationsKeys.common_close)}
          onPrimaryClick={() => {
            setShowDeleteSurveyConfirmation(false);
            onDelete && onDelete();
          }}
          onSecondaryClick={() => setShowDeleteSurveyConfirmation(false)}
        />
      )}
    </>
  );
};
